import axios from 'axios';

// Get backend URL from environment - REQUIRED for deployment
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

if (!BACKEND_URL) {
  console.error('=== CRITICAL: REACT_APP_BACKEND_URL is not set! ===');
  console.error('API calls will fail. Please ensure .env file exists with REACT_APP_BACKEND_URL');
}

console.log('=== API Client Initializing ===');
console.log('Backend URL:', BACKEND_URL || 'NOT SET!');

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  timeout: 180000, // 3 minute timeout for large requests (like batch label printing)
  headers: {
    'Content-Type': 'application/json'
  }
});

// =====================================================================
// Transient rate-limit (HTTP 429) retry — with exponential backoff
// =====================================================================
// In the warehouse, many scan/print stations sit behind ONE NAT'd public
// IP, so their combined request bursts trip the ingress's shared per-IP
// rate limit and come back as HTTP 429 ("Too Many Requests"). The worst
// offender is the Combo PP Scan Station print flow, which fires ~5 rapid
// calls per submit (label-data → preview-batch → mark-printed →
// my-handover → sessions). A single throttled call used to break the
// whole print and force the operator to reprint.
//
// A 429 is rejected AT THE INGRESS before the backend ever runs, so the
// request had NO side effects — making an automatic retry safe even for
// non-idempotent endpoints like /label-data (which claims FG USNs). We
// retry 429 ONLY (never 5xx, which may have partially executed).
const RETRYABLE_STATUSES = [429];
const MAX_RETRIES = 4;

const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Honour the server's `Retry-After` (seconds or HTTP-date); capped at 15s.
const _parseRetryAfter = (headerVal) => {
  if (!headerVal) return null;
  const asInt = Number(headerVal);
  if (!Number.isNaN(asInt)) return Math.min(asInt * 1000, 15000);
  const asDate = Date.parse(headerVal);
  if (!Number.isNaN(asDate)) return Math.max(0, Math.min(asDate - Date.now(), 15000));
  return null;
};

// Exponential backoff with jitter: ~0.6s, 1.2s, 2.4s, 4.8s (cap 8s).
const _computeBackoff = (attempt, retryAfterMs) => {
  if (retryAfterMs != null) return retryAfterMs;
  const base = 600 * Math.pow(2, attempt);
  const jitter = Math.random() * 300;
  return Math.min(base + jitter, 8000);
};

// Returns the retry promise if this error is a retryable 429 under the
// cap, otherwise null (caller then rejects as normal).
const _maybeRetry = (instance, error) => {
  const config = error.config;
  const status = error.response?.status;
  if (!config || !RETRYABLE_STATUSES.includes(status)) return null;
  config.__retryCount = config.__retryCount || 0;
  if (config.__retryCount >= MAX_RETRIES) return null;
  config.__retryCount += 1;
  const retryAfterMs = _parseRetryAfter(error.response?.headers?.['retry-after']);
  const delay = _computeBackoff(config.__retryCount - 1, retryAfterMs);
  console.warn(`[API] ${status} on ${config.url} — retry ${config.__retryCount}/${MAX_RETRIES} in ${Math.round(delay)}ms`);
  return _sleep(delay).then(() => instance(config));
};

// Request interceptor - add token to every request
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('session_token');
    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    console.log(`[API] Token in localStorage: ${token ? token.substring(0, 20) + '...' : 'NONE'}`);
    
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
      console.log('[API] Authorization header added');
    } else {
      console.warn('[API] NO TOKEN - Request will fail authentication!');
    }

    // Attach scanned Staff-ID for the 5 operational modules that are
    // gated by `staff_scan_dep`. The module operator scans their Staff-ID
    // on the page (via <StaffScanGate />) which sets `sessionStorage.staff_id`.
    // The backend will 400 if this header is missing, so we only attach
    // it when set (non-module GETs / login flows can still proceed).
    const staffId = sessionStorage.getItem('module_staff_id');
    if (staffId) {
      config.headers['X-Staff-Id'] = staffId;
    }
    // Phase 1 multi-site — attach the active site_id selected on the
    // login site picker. Backend strict-validates this header for every
    // site-aware endpoint. If the user hasn't been through the picker
    // (direct URL, cleared localStorage, etc.), fall back to 'salem'
    // — the documented default site — so legacy single-site URLs keep
    // working instead of surfacing a raw HTTP 400 to the operator.
    const siteId = localStorage.getItem('active_site_id') || 'salem';
    config.headers['X-Site-Id'] = siteId;
    return config;
  },
  (error) => {
    console.error('[API] Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - handle auth errors
apiClient.interceptors.response.use(
  (response) => {
    console.log(`[API] Response ${response.config.url}: ${response.status} OK`);
    return response;
  },
  (error) => {
    console.error(`[API] Error ${error.config?.url}: ${error.response?.status}`, error.response?.data);

    // Auto-retry transient 429s (see _maybeRetry above) before anything else.
    const retryPromise = _maybeRetry(apiClient, error);
    if (retryPromise) return retryPromise;

    // Only redirect on 401 if user was logged in and not on auth pages
    if (error.response?.status === 401) {
      const _url = error.config?.url || '';
      const isAuthEndpoint = _url.includes('/auth/');
      // Step-up credential checks (approver re-verification) legitimately return
      // 401 on a wrong password — they must NOT log the current user out.
      const isStepUp = /recheckin|verify-admin|verify-stepup/.test(_url);
      const isOnAuthPage = window.location.pathname.includes('/login') || 
                          window.location.pathname.includes('/auth/callback');
      const hasToken = localStorage.getItem('session_token');
      
      console.log(`[API] 401 Error - isAuthEndpoint: ${isAuthEndpoint}, isStepUp: ${isStepUp}, isOnAuthPage: ${isOnAuthPage}, hasToken: ${!!hasToken}`);
      
      if (!isAuthEndpoint && !isStepUp && !isOnAuthPage && hasToken) {
        console.log('[API] Session expired - clearing storage and redirecting to login');
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// =====================================================================
// GLOBAL axios safety net (Phase 5 hardening)
// =====================================================================
// Several legacy modules (Combo V2 planner / picker / distributor /
// reversal / staff-rankings — 12 files, 36+ call sites) import axios
// directly instead of this `apiClient`. Without an interceptor on the
// GLOBAL axios instance, those calls miss `X-Site-Id` and silently route
// every write to Salem regardless of which site the operator picked.
//
// We attach a request interceptor on the global axios that:
//   * auto-injects Authorization from localStorage if the call is hitting
//     OUR backend (matched by hostname / `/api/`)
//   * auto-injects X-Site-Id from localStorage.active_site_id
//   * never overwrites a header the caller explicitly set
//
// Skipped for absolute external URLs so we don't leak internal tokens to
// third-party domains.
// =====================================================================
axios.interceptors.request.use((config) => {
  try {
    const url = String(config.url || '');
    const baseURL = String(config.baseURL || '');
    // Only patch calls that target OUR backend.
    const isOurApi = url.includes('/api/') || baseURL.includes(BACKEND_URL || '_');
    const goesToBackend = (BACKEND_URL && (url.startsWith(BACKEND_URL) || baseURL.startsWith(BACKEND_URL)))
      || (url.startsWith('/api/'));
    if (!isOurApi && !goesToBackend) return config;

    config.headers = config.headers || {};
    if (!config.headers.Authorization && !config.headers.authorization) {
      const token = localStorage.getItem('session_token');
      if (token) config.headers.Authorization = `Bearer ${token}`;
    }
    if (!config.headers['X-Site-Id'] && !config.headers['x-site-id']) {
      const siteId = localStorage.getItem('active_site_id') || 'salem';
      config.headers['X-Site-Id'] = siteId;
    }
    if (!config.headers['X-Staff-Id'] && !config.headers['x-staff-id']) {
      const staffId = sessionStorage.getItem('module_staff_id');
      if (staffId) config.headers['X-Staff-Id'] = staffId;
    }
  } catch (e) {
    console.warn('[GLOBAL-AXIOS-INTERCEPTOR] header injection skipped:', e);
  }
  return config;
});

// GLOBAL axios response interceptor — same 429 retry-with-backoff as
// apiClient, so the many legacy pages that import `axios` directly
// (Combo V2 PP Station submit/scan/handover, planner, distributor, etc.)
// also survive a transient ingress rate-limit instead of surfacing a raw
// "Request failed with status code 429" to the operator.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const retryPromise = _maybeRetry(axios, error);
    if (retryPromise) return retryPromise;
    return Promise.reject(error);
  }
);

// =====================================================================
// GLOBAL window.fetch safety net (Phase 5.1 hardening)
// =====================================================================
// 18 call sites across legacy pages (FGPickSlipUpload, OrderManagement,
// PackingWorkstation, USNMasterExport, ReStickerQueue SSE, etc.) use raw
// `fetch()` to talk to /api/... — these bypass axios entirely. Without
// this wrap they ship without `Authorization` or `X-Site-Id`, so the
// backend's strict site-id middleware rejects them with HTTP 400, which
// the operator sees as the "Missing required 'X-Site-Id' request header"
// red toast on FG Picking + other WMS pages.
//
// Wrap once on app boot. Only patches calls aimed at OUR backend (so
// CDN / 3rd-party fetches go through untouched).
// =====================================================================
if (typeof window !== 'undefined' && !window.__emergent_fetch_patched) {
  const _origFetch = window.fetch.bind(window);
  window.fetch = function patchedFetch(input, init) {
    try {
      const url = typeof input === 'string'
        ? input
        : (input && input.url) || '';
      const goesToBackend =
        url.startsWith('/api/')
        || (BACKEND_URL && url.startsWith(BACKEND_URL))
        // Same-origin relative path that targets /api/ via React proxy.
        || (url.includes('/api/') && !url.match(/^https?:\/\//));
      if (!goesToBackend) return _origFetch(input, init);

      // Merge headers without clobbering anything the caller set.
      const callerHeaders = new Headers((init && init.headers) || {});
      if (!callerHeaders.has('Authorization') && !callerHeaders.has('authorization')) {
        const tok = localStorage.getItem('session_token');
        if (tok) callerHeaders.set('Authorization', `Bearer ${tok}`);
      }
      if (!callerHeaders.has('X-Site-Id') && !callerHeaders.has('x-site-id')) {
        callerHeaders.set('X-Site-Id', localStorage.getItem('active_site_id') || 'salem');
      }
      if (!callerHeaders.has('X-Staff-Id') && !callerHeaders.has('x-staff-id')) {
        const staffId = sessionStorage.getItem('module_staff_id');
        if (staffId) callerHeaders.set('X-Staff-Id', staffId);
      }
      const newInit = { ...(init || {}), headers: callerHeaders };
      return _origFetch(input, newInit);
    } catch (e) {
      console.warn('[GLOBAL-FETCH-INTERCEPTOR] header injection skipped:', e);
      return _origFetch(input, init);
    }
  };
  window.__emergent_fetch_patched = true;
}

export default apiClient;
